import React from 'react';
import { Button, Checkbox, Icon } from '@gerdau/hefesto';
import { Table } from '@gerdau/hefesto-mais-web';
import { EUploadProjectOption } from '..';
import FabricationPavementSelect from '../../../../../../components/FabricationPavementsSelect/indexFacelift';
import { IFabricationProjectsState } from '../../../../../../store/ducks/fabrication-projects/types';
import {
  getDefaultFabricationProjectItem,
  IFabricationProjectItem
} from '../../../../../../store/ducks/fabrication-purchase-order/types';
import FabricationProjectSelect from './FabricationProjectSelect';
import { projectPavementsNeedRange } from '../../../../../../utils/fabricationHelper';
import FabricationDebouncedTextField from './FabricationDebouncedTextField';
import FabricationDebouncedStepper from './FabricationDebouncedStepper';

interface IDetailingProjectItemProps {
  fabricationProjectsState: IFabricationProjectsState;
  items: IFabricationProjectItem[];
  updateItem(item: IFabricationProjectItem): void;
  onRemoveItem(key: string): void;
  isTheOnlyItem?: boolean;
  uploadProjectOption?: EUploadProjectOption;
}

const DetailingProjectTable: React.FC<IDetailingProjectItemProps> = ({
  fabricationProjectsState,
  items,
  updateItem,
  onRemoveItem,
  isTheOnlyItem,
  uploadProjectOption
}) => {
  const dataSource = items.map((item) => ({
    ...item
  }));

  const getColumns = () => {
    const columns = [
      {
        title: 'Arquivo do projeto',
        dataIndex: 'fileProject',
        width: '30rem',
        render: (_: string, item: IFabricationProjectItem) => {
          return (
            <FabricationProjectSelect
              projects={fabricationProjectsState.projects || []}
              placeholder={'Selecione um projeto'}
              onSelectProject={(projectId) =>
                onSelectProject(projectId, item.key)
              }
              selectedProject={item.projectSelected}
              uploadProjectOption={uploadProjectOption}
              loading={fabricationProjectsState.loading}
            />
          );
        }
      },
      {
        title: 'Revisão',
        dataIndex: 'review',
        width: '15rem',
        render: (_: string, item: IFabricationProjectItem) => (
          <FabricationDebouncedTextField
            label=""
            name="inputReview"
            hideLabel
            value={item?.revision}
            disabled={!item.projectSelected || !!item.projectSelected.revision}
            onChange={(e) => updateState('revision', e.target.value, item)}
          />
        )
      },
      {
        title: 'Pavimentos',
        dataIndex: 'pavements',
        width: '30rem',
        render: (_: string, item: IFabricationProjectItem) => (
          <>
            {!item.projectSelected?.pavements?.length ? (
              <FabricationDebouncedTextField
                label=""
                name="inputPavement"
                placeholder="Digite o pavimento"
                value={item?.pavement}
                onChange={(e) => updateState('pavement', e.target.value, item)}
                disabled={!item.projectSelected}
              />
            ) : (
              <FabricationPavementSelect
                onChange={(selectedPavementIds) =>
                  onSelectPavements(selectedPavementIds, item)
                }
                pavements={item.pavements.map((p) => p.id)}
                pavementsOptions={item?.projectSelected.pavements || []}
                isPavementRangeNeeded={
                  item.projectSelected &&
                  projectPavementsNeedRange(
                    item.projectSelected.structures.map((s) => s.name)
                  )
                }
                disabled={!item.projectId}
              />
            )}
          </>
        )
      },
      {
        title: 'Nº de repetições',
        dataIndex: 'numberRepetitions',
        width: '15rem',
        align: 'center' as const,
        render: (_: string, item: IFabricationProjectItem) => (
          <FabricationDebouncedStepper
            value={item?.amount || 0}
            disabled={!item.projectId}
            onChange={(value) => updateState('amount', value, item)}
            onIncreaseClick={() => onIncrease(item)}
            onDecreaseClick={() => onDecrease(item)}
          />
        )
      },
      {
        title: 'Desenho completo',
        dataIndex: 'completeDrawing',
        width: '10rem',
        align: 'center' as const,
        render: (_: string, item: IFabricationProjectItem) => (
          <Checkbox
            name="completeDrawingCheckbox"
            checked={item?.completeDrawing}
            onChange={() => onChangeCompleteDrawing(item)}
            disabled={!item.projectSelected}
          />
        )
      },
      {
        title: 'Detalhes dos elementos estruturais',
        dataIndex: 'detailsStructuralElements',
        width: '30rem',
        render: (_: string, item: IFabricationProjectItem) => (
          <FabricationDebouncedTextField
            label=""
            hideLabel
            name="detailsStructuralElementsInput"
            placeholder="Blocos, lajes, pilares, vigas, entre outros"
            value={item?.observation}
            disabled={item?.completeDrawing || !item.projectId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateState('observation', e.target.value, item)
            }
            maxLength={300}
            supportMessage={
              item?.observation.length == 300
                ? 'Deve ter no máximo 300 caracteres'
                : ''
            }
          />
        )
      },
      {
        title: '',
        dataIndex: 'key',
        width: '1rem',
        align: 'center' as const,
        render: (key: string, item: IFabricationProjectItem) => (
          <>
            {canShowRemoveButton(item) && (
              <Button
                name="deleteButton"
                variant="neutral"
                icon={<Icon option="Trash" variant="danger" />}
                onClick={() => onRemoveItem(key)}
              ></Button>
            )}
          </>
        )
      }
    ];
    return columns;
  };

  const updateState = (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValue: any,
    updatedItem: IFabricationProjectItem
  ) => {
    const updatedProjectItem = {
      ...updatedItem,
      [field]: newValue
    };

    updateItem(updatedProjectItem);
  };

  const getSelectedProject = (projectId?: number) => {
    if (!projectId) {
      return;
    }
    return fabricationProjectsState.projects?.find((p) => p.id === projectId);
  };

  const onSelectProject = (projectId: number, key: string) => {
    const projectSelected = getSelectedProject(projectId);
    if (!projectSelected) {
      return;
    }

    const updatedProjectItem: IFabricationProjectItem = {
      ...getDefaultFabricationProjectItem(),
      projectId,
      revision: projectSelected.revision || '',
      key,
      projectSelected
    };

    updateItem(updatedProjectItem);
  };

  const onSelectPavements = (
    selectedPavementIds: number[],
    updatedItem: IFabricationProjectItem
  ) => {
    const selectedPavements = updatedItem.projectSelected?.pavements
      .filter((p) => selectedPavementIds.includes(Number(p.id)))
      .sort((a, b) => a.position - b.position);

    const pavements: string[] = [];
    const selectedProject = getSelectedProject(updatedItem.projectId);
    const isRangeNeeded =
      selectedProject &&
      projectPavementsNeedRange(selectedProject.structures.map((s) => s.name));
    if (isRangeNeeded && selectedPavements && selectedPavements.length > 0) {
      pavements.push(selectedPavements[0].name);

      if (selectedPavements.length > 1) {
        pavements.push(
          ' ao ' + selectedPavements[selectedPavements.length - 1].name
        );
      }
    } else {
      selectedPavements?.forEach((p) => pavements.push(p.name));
    }

    const updatedProjectItem: IFabricationProjectItem = {
      ...updatedItem,
      pavement: isRangeNeeded ? pavements.join('') : pavements.join(', '),
      pavements: selectedPavements || []
    };

    updateItem(updatedProjectItem);
  };

  const onChangeCompleteDrawing = (updatedItem: IFabricationProjectItem) => {
    const updatedProjectItem: IFabricationProjectItem = {
      ...updatedItem,
      completeDrawing: !updatedItem.completeDrawing,
      observation: ''
    };

    updateItem(updatedProjectItem);
  };

  const canShowRemoveButton = (item: IFabricationProjectItem) => {
    const { projectSelected } = item;
    return !isTheOnlyItem || projectSelected !== undefined;
  };

  const onIncrease = (item: IFabricationProjectItem) => {
    updateState('amount', (item.amount || 0) + 1, item);
  };
  const onDecrease = (item: IFabricationProjectItem) => {
    if (item.amount && item.amount > 0) {
      updateState('amount', item.amount - 1, item);
    }
  };

  return <Table columns={getColumns()} dataSource={dataSource}></Table>;
};

export default DetailingProjectTable;
